import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCreateDrink, usePatchDrink } from "@/api/hooks/useDrinks";
import {
  DRINK_STATUSES,
  DRINK_STATUS_LABELS,
  SPIRIT_BASES,
  SPIRIT_BASE_LABELS,
  type Drink,
  type DrinkStatus,
  type SpiritBase,
} from "@/api/types/drink";
import {
  draftsToIngredients,
  emptyIngredientDraft,
  ingredientsToDrafts,
  type IngredientDraft,
} from "./ingredientDraft";
import { IngredientsEditor } from "./IngredientsEditor";

interface DrinkEditorDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present when editing an existing library drink; absent when creating. */
  drink?: Drink | null;
  onSaved?: (drink: Drink) => void;
}

/**
 * Create/edit dialog for a library drink: name, story, spirit base,
 * ingredients editor, an NA-variant toggle that reveals a second
 * ingredients editor for the zero-proof twin recipe, and status.
 */
export function DrinkEditorDialog({ open, onClose, drink, onSaved }: DrinkEditorDialogProps) {
  const isEdit = !!drink;
  const createDrink = useCreateDrink();
  const patchDrink = usePatchDrink();
  const isSaving = createDrink.isPending || patchDrink.isPending;

  const [name, setName] = useState("");
  const [story, setStory] = useState("");
  const [spiritBase, setSpiritBase] = useState<SpiritBase>("vodka");
  const [status, setStatus] = useState<DrinkStatus>("active");
  const [ingredients, setIngredients] = useState<IngredientDraft[]>([]);
  const [hasNaVariant, setHasNaVariant] = useState(false);
  const [naRecipe, setNaRecipe] = useState<IngredientDraft[]>([]);

  useEffect(() => {
    if (!open) return;
    if (drink) {
      setName(drink.name);
      setStory(drink.story ?? "");
      setSpiritBase(drink.spirit_base);
      setStatus(drink.status);
      setIngredients(
        ingredientsToDrafts(drink.ingredients).length > 0
          ? ingredientsToDrafts(drink.ingredients)
          : [emptyIngredientDraft()],
      );
      setHasNaVariant(drink.has_na_variant);
      setNaRecipe(
        drink.na_recipe && drink.na_recipe.length > 0
          ? ingredientsToDrafts(drink.na_recipe)
          : [emptyIngredientDraft()],
      );
      return;
    }
    setName("");
    setStory("");
    setSpiritBase("vodka");
    setStatus("draft");
    setIngredients([emptyIngredientDraft()]);
    setHasNaVariant(false);
    setNaRecipe([emptyIngredientDraft()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, drink?.id]);

  const canSubmit = name.trim() !== "" && draftsToIngredients(ingredients).length > 0;

  async function handleSubmit() {
    const payload = {
      name: name.trim(),
      story: story.trim() ? story.trim() : null,
      ingredients: draftsToIngredients(ingredients),
      spirit_base: spiritBase,
      has_na_variant: hasNaVariant,
      na_recipe: hasNaVariant ? draftsToIngredients(naRecipe) : null,
      status,
    };

    try {
      if (isEdit && drink) {
        const updated = await patchDrink.mutateAsync({ id: drink.id, ...payload });
        toastSuccess("Drink updated");
        onSaved?.(updated);
      } else {
        const created = await createDrink.mutateAsync(payload);
        toastSuccess("Drink created");
        onSaved?.(created);
      }
      onClose();
    } catch (err) {
      toastError("Couldn't save drink", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel={isEdit ? "Edit drink" : "New drink"}>
      <DialogContent size="xl">
        <DialogHeader onClose={onClose}>{isEdit ? "Edit drink" : "New drink"}</DialogHeader>
        <DialogBody className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Name"
              required
              value={name}
              disabled={isSaving}
              onChange={(e) => setName(e.target.value)}
            />
            <FormSelect
              label="Spirit base"
              required
              value={spiritBase}
              disabled={isSaving}
              onChange={(e) => setSpiritBase(e.target.value as SpiritBase)}
            >
              {SPIRIT_BASES.map((base) => (
                <option key={base} value={base}>
                  {SPIRIT_BASE_LABELS[base]}
                </option>
              ))}
            </FormSelect>
          </div>

          <FormTextarea
            label="Story"
            value={story}
            disabled={isSaving}
            onChange={(e) => setStory(e.target.value)}
            hint="One or two sentences — this is what appears on the menu/story card."
          />

          <IngredientsEditor
            label="Ingredients"
            items={ingredients}
            onChange={setIngredients}
            onAdd={() => setIngredients((prev) => [...prev, emptyIngredientDraft()])}
            disabled={isSaving}
            testIdPrefix="ingredients"
          />

          <Checkbox
            label="Has a zero-proof (NA) variant"
            checked={hasNaVariant}
            disabled={isSaving}
            onChange={(e) => setHasNaVariant(e.target.checked)}
          />

          {hasNaVariant && (
            <IngredientsEditor
              label="Zero-proof recipe"
              items={naRecipe}
              onChange={setNaRecipe}
              onAdd={() => setNaRecipe((prev) => [...prev, emptyIngredientDraft()])}
              disabled={isSaving}
              testIdPrefix="na-recipe"
            />
          )}

          <FormSelect
            label="Status"
            required
            value={status}
            disabled={isSaving}
            onChange={(e) => setStatus(e.target.value as DrinkStatus)}
          >
            {DRINK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {DRINK_STATUS_LABELS[s]}
              </option>
            ))}
          </FormSelect>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={!canSubmit || isSaving}>
            {isSaving ? "Saving..." : isEdit ? "Save changes" : "Create drink"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
